import React, { useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'
import cn from 'clsx'
import { rem } from 'polished'
import CalendarDatePicker from './BedbankDatePicker/CalendarDatePicker'
import OfferDatePicker from '../Filters/DatePicker/OfferDatePicker'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { isFlashCalendarEnabled, getSelectedDay } from 'selectors/offerPage/bedbankOfferPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import TextButton from 'components/Luxkit/Button/TextButton'
import { displayPricingAs, getCalendarDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import { sum } from 'lib/array/arrayUtils'
import eases from 'styles/tools/eases'
import { pluralizeToString } from 'lib/string/pluralize'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_SHORT_DAY_NAME_DAY_MONTH } from 'constants/dateFormats'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import noop from 'lib/function/noop'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

const FooterContents = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  transform: translateY(100%);
  transition: transform 0.3s ${eases.expo.in};

  &.visible {
    position: unset;
    transform: translateY(0);
    transition: transform 0.3s ${eases.expo.out};
  }
`

const CalendarDatePickerWrapper = styled.div`
  padding-top: ${rem(20)};
`

interface Props {
  isCalendarEnabled: boolean;
  selectedDay?: App.BedbankCalendarDay;
  rooms?: Array<App.Occupants>;
  duration: number;
  flights?: boolean;
  offer: App.BedbankOffer;
  onClose?: () => void;
  onBack?: () => void;
}

function BedbankSidebarFilterCalendar(props: Props) {
  const {
    offer,
    isCalendarEnabled,
    selectedDay,
    rooms,
    duration,
    flights,
    onClose = noop,
    onBack,
  } = props

  const dispatch = useOfferPageDispatch()
  const intlDateFormatter = useIntlDateFormatter()
  const modalContext = useModalElementContext()

  // const [selectedMonth, setSelectedMonth] = useState<string | undefined>(selectedDay?.checkIn)

  const adultsNumber = useMemo(() => sum(rooms ?? [], room => room.adults), [rooms])

  const {
    total,
    saleUnit,
  } = getCalendarDisplayTotalPrice({
    price: selectedDay?.price ?? 0,
    adultsNumber,
    nights: duration,
    roomsNumber: rooms?.length ?? 1,
    withFlights: !!flights,
    flightPrice: selectedDay?.flightPrice ?? 0,
    displayPricingPer: displayPricingAs(offer.displayPricingAsPerPerson),
  })

  const onResetDates = useCallback(() => {
    dispatch({ type: OfferPageActions.resetDates })
  }, [dispatch])

  const onApply = useCallback(() => {
    modalContext.resolve()
  }, [modalContext])

  const onModalClose = useCallback(() => {
    onClose()
    modalContext.resolve()
  }, [onClose, modalContext])

  const scrollerRef = useRef<HTMLDivElement>(null)

  // temporarily disabled until we have updated designs for the month view when empty
  // const onShowMonth = useCallback(() => setSelectedMonth(undefined), [])
  // const onSelectMonth = useCallback<AccommodationCalendarYearSelectHandler>((month) => {
  //   setSelectedMonth(formatIsoDate(new Date(month.year, month.month, 1)))
  // }, [])

  return (
    <>
      <ModalHeader
        title="Select your dates"
        onCloseButtonClick={onModalClose}
        onBackButtonClick={onBack}
      >
      </ModalHeader>
      <ModalBody ref={scrollerRef}>
        <ModalContent>
          <CalendarDatePickerWrapper>
            {isCalendarEnabled && <CalendarDatePicker single />}
            {!isCalendarEnabled && <OfferDatePicker
              scrollerRef={scrollerRef}
              size="small"
              // onBackToMonth={onShowMonth}
              // onMonthSelect={onSelectMonth}
              // selectedMonth={selectedMonth}
              variant="day-only"
            />}
          </CalendarDatePickerWrapper>
        </ModalContent>
      </ModalBody>
      <FooterContents className={cn({ visible: !!selectedDay })}>
        <ModalFooter>
          <VerticalSpacer gap={8}>
            {selectedDay && <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
              <BodyText variant="medium">
                <Group direction="horizontal" verticalAlign="center">
                  <span>{intlDateFormatter(selectedDay.checkIn, INTL_SHORT_DAY_NAME_DAY_MONTH)}</span>
                  <LineArrowRightIcon size="XS" />
                  <span>{intlDateFormatter(selectedDay.checkOut, INTL_SHORT_DAY_NAME_DAY_MONTH)}</span>
                </Group>
              </BodyText>
              <TextButton kind="tertiary" horizontalOutdent="end" onClick={onResetDates}>
                Reset
              </TextButton>
            </Group>}
            <Group direction="horizontal" horizontalAlign="space-between">
              {selectedDay?.price && selectedDay?.hasPromo && <div>
                <Caption variant="large">
                  {pluralizeToString('night', duration)} {flights && '+ flights '} {' from'}
                </Caption>
                <Heading as="span" variant="heading5">
                  <FormatCurrency value={total} />
                  {offer.displayPricingAsPerPerson && <Caption variant="medium" as="span">{saleUnit}</Caption>}
                </Heading>{' '}
                {!offer.displayPricingAsPerPerson && <Caption as="span" variant="large">{saleUnit}</Caption>}
              </div>}
              <TextButton kind="primary" size="large" type="submit" fit="mobile-full-width" onClick={onApply}>
                Apply
              </TextButton>
            </Group>
          </VerticalSpacer>
        </ModalFooter>
      </FooterContents>
    </>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps: Partial<Props>) => ({
  offer: state.bedbankOffer!,
  isCalendarEnabled: isFlashCalendarEnabled({ ...state, bedbankOffer: ownProps.offer }, appState),
  selectedDay: getSelectedDay(state, appState),
  rooms: state.rooms,
  duration: state.duration,
  flights: state.flights,
}), BedbankSidebarFilterCalendar)
